import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CompanyBrandingSection } from '../components/CompanyBrandingSection';
import { AMS360Configuration } from '../components/admin/AMS360Configuration';
import { ams360Service } from '../services/ams360Service';
import { TemplateLibraryView } from '../components/TemplateLibraryView';
import CompanyPromptTemplates from '../components/admin/CompanyPromptTemplates';
import UsageReportsSection from '../components/UsageReportsSection';

interface CompanyUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: 'pending' | 'active' | 'disabled';
  role: 'user' | 'admin';
  lineOfBusiness?: 'personal' | 'commercial' | 'both';
  consumesLicense?: boolean;
  createdAt: string;
  twoFactorEnabled?: boolean;
}

interface CreateUserForm {
  email: string;
  firstName: string;
  lastName: string;
  role: 'user' | 'admin';
  consumesLicense: boolean;
}

interface CompanyAddress {
  id?: string;
  addressType: 'primary' | 'secondary' | 'billing' | 'shipping' | 'office' | 'other';
  label?: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isDefault: boolean;
}

interface CompanyBranding {
  logoUrl?: string | null;
  logoS3Key?: string | null;
  logoPosition?: 'left' | 'center' | 'right';
  logoOnAllPages?: boolean;
  companyDisplayName?: string | null;
  headerText?: string | null;
  footerText?: string | null;
}

interface Company {
  id: string;
  name: string;
  domain: string;
  licenseCount: number;
  addresses?: CompanyAddress[];
  branding?: CompanyBranding;
}

type AdminSection = 'company' | 'branding' | 'ams360' | 'templates' | 'prompt-templates' | 'reports' | 'users';

const Admin: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<AdminSection>('company');
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'status'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Click a column header: toggle direction if it's the active column, else
  // switch to that column ascending.
  const toggleSort = (col: 'name' | 'status') => {
    if (sortBy === col) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };
  const [licenseCount, setLicenseCount] = useState<number>(0);
  const [used, setUsed] = useState<number>(0);
  const [available, setAvailable] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<CreateUserForm>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'user',
    consumesLicense: true
  });

  // Company address management state
  const [company, setCompany] = useState<Company | null>(null);
  const [isAms360Enabled, setIsAms360Enabled] = useState<boolean>(false);

  // Profile-edit modal
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null);
  const [editForm, setEditForm] = useState<{ firstName: string; lastName: string; email: string }>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [editSaving, setEditSaving] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [showAddressForm, setShowAddressForm] = useState<boolean>(false);
  const [editingAddress, setEditingAddress] = useState<CompanyAddress | null>(null);
  const [addressFormData, setAddressFormData] = useState<CompanyAddress>({
    addressType: 'office',
    label: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United States',
    isDefault: false
  });

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin' && user.role !== 'system_admin') {
      navigate('/');
      return;
    }
    fetchUsers();
    fetchCompanyInfo();
    // Gate the AMS360 admin tab — only show when SysAdmin has flipped the
    // company's `ams360Enabled` flag. The settings endpoint returns 200 with
    // `enabled: false` when the company isn't AMS360-enabled.
    ams360Service.getSettings()
      .then((s) => setIsAms360Enabled(!!s.enabled))
      .catch(() => setIsAms360Enabled(false));
  }, [user, token, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `Failed to fetch users (${res.status})`);
      }
      
      // Handle new response structure
      setUsers(data.users);
      if (data.licenseInfo) {
        setLicenseCount(data.licenseInfo.total);
        setUsed(data.licenseInfo.used);
        setAvailable(data.licenseInfo.available);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const activateUser = async (userId: string) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/users/${userId}/activate`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to activate user');
      
      // Refresh users list to get updated counts
      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error activating user');
    }
  };

  const deactivateUser = async (userId: string) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/users/${userId}/deactivate`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to deactivate user');
      
      // Refresh users list to get updated counts
      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error deactivating user');
    }
  };

  const changeUserPassword = async (userId: string, newPassword: string) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/users/${userId}/password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ newPassword }),
      });
      if (!res.ok) throw new Error('Failed to change user password');
      
      alert('User password changed successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error changing user password');
    }
  };

  const deleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete user');
      
      // Refresh users list to get updated counts
      await fetchUsers();
      alert('User deleted successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error deleting user');
    }
  };

  const changeUserRole = async (userId: string, newRole: 'user' | 'admin') => {
    const verb = newRole === 'admin' ? 'promote this user to admin' : 'change this admin back to a regular user';
    if (!window.confirm(`Are you sure you want to ${verb}?`)) {
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change user role');

      // Refresh users list to reflect the new role
      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error changing user role');
    }
  };

  const handlePasswordChange = (userId: string) => {
    const newPassword = prompt('Enter new password (minimum 6 characters):');
    if (newPassword && newPassword.length >= 6) {
      changeUserPassword(userId, newPassword);
    } else if (newPassword !== null) {
      alert('Password must be at least 6 characters long');
    }
  };

  const resetUser2FA = async (userId: string) => {
    if (!window.confirm('Are you sure you want to reset this user\'s two-factor authentication? They will need to set it up again.')) {
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/users/${userId}/2fa/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to reset user 2FA');
      }

      // Refresh users list to get updated status
      await fetchUsers();
      alert('User 2FA reset successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error resetting user 2FA');
    }
  };

  const setUserConsumesLicense = async (userId: string, consumesLicense: boolean) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/users/${userId}/license`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ consumesLicense }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update user license setting');
      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error updating user license setting');
    }
  };

  const updateUserLineOfBusiness = async (userId: string, lineOfBusiness: string) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/users/${userId}/line-of-business`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ lineOfBusiness }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update user line of business');

      // Refresh users list
      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error updating user line of business');
    }
  };

  const openEditUser = (u: CompanyUser) => {
    setEditingUser(u);
    setEditForm({ firstName: u.firstName, lastName: u.lastName, email: u.email });
    setEditError(null);
  };

  const closeEditUser = () => {
    setEditingUser(null);
    setEditError(null);
  };

  const saveEditUser = async () => {
    if (!editingUser) return;
    setEditSaving(true);
    setEditError(null);
    try {
      // Only send changed fields so we don't trip unique-email checks when the
      // admin only meant to fix a typo in the name.
      const body: Record<string, string> = {};
      if (editForm.firstName.trim() !== editingUser.firstName) body.firstName = editForm.firstName.trim();
      if (editForm.lastName.trim() !== editingUser.lastName) body.lastName = editForm.lastName.trim();
      if (editForm.email.trim().toLowerCase() !== editingUser.email.toLowerCase()) body.email = editForm.email.trim();

      if (Object.keys(body).length === 0) {
        closeEditUser();
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/users/${editingUser.id}/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update user profile');
      await fetchUsers();
      closeEditUser();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update user profile');
    } finally {
      setEditSaving(false);
    }
  };

  // Company address management functions
  const fetchCompanyInfo = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/company`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (res.ok) {
        const companyData = await res.json();
        setCompany(companyData);
      }
    } catch (err) {
      console.error('Error fetching company info:', err);
    }
  };

  const handleAddressFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = editingAddress 
        ? `${process.env.REACT_APP_API_BASE_URL || '/api'}/addresses/user/${editingAddress.id}`
        : `${process.env.REACT_APP_API_BASE_URL || '/api'}/addresses/user`;
        
      const res = await fetch(url, {
        method: editingAddress ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(addressFormData)
      });

      if (res.ok) {
        await fetchCompanyInfo();
        resetAddressForm();
        alert(editingAddress ? 'Address updated successfully!' : 'Address added successfully!');
      } else {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save address');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error saving address');
    }
  };

  const handleEditAddress = (address: CompanyAddress) => {
    setEditingAddress(address);
    setAddressFormData(address);
    setShowAddressForm(true);
  };

  const handleDeleteAddress = async (addressId: string) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/addresses/user/${addressId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchCompanyInfo();
        alert('Address deleted successfully!');
      } else {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete address');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error deleting address');
    }
  };

  const handleSetDefaultAddress = async (addressId: string) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/addresses/user/${addressId}/set-default`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchCompanyInfo();
        alert('Default address updated successfully!');
      } else {
        const data = await res.json();
        throw new Error(data.error || 'Failed to set default address');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error setting default address');
    }
  };

  const resetAddressForm = () => {
    setShowAddressForm(false);
    setEditingAddress(null);
    setAddressFormData({
      addressType: 'office',
      label: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'United States',
      isDefault: false
    });
  };

  const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setAddressFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to create user');
      }
      
      // Reset form and refresh users list
      setFormData({ email: '', firstName: '', lastName: '', role: 'user', consumesLicense: true });
      setShowCreateForm(false);
      await fetchUsers();

      alert('User created successfully! A temporary password has been emailed to them.');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error creating user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 p-6">{error}</div>;
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen w-full">
      {/* Page header */}
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">Company Administration</p>
              <h1 className="text-2xl font-bold text-secondary dark:text-dark-text mt-1">{company?.name || 'Admin Console'}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Manage users, branding, integrations, and reporting for your organization.
              </p>
            </div>
            <button
              onClick={() => navigate('/admin/billing')}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600 transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              Billing Portal
            </button>
          </div>
        </div>
        {/* Section nav */}
        <div className="max-w-6xl mx-auto px-6">
          <nav className="flex flex-wrap gap-1 -mb-px">
            {([
              { id: 'company' as AdminSection, label: 'Company' },
              { id: 'branding' as AdminSection, label: 'Branding' },
              ...(isAms360Enabled ? [{ id: 'ams360' as AdminSection, label: 'AMS360' }] : []),
              { id: 'templates' as AdminSection, label: 'Templates' },
              { id: 'prompt-templates' as AdminSection, label: 'Prompt Templates' },
              { id: 'reports' as AdminSection, label: 'Reports' },
              { id: 'users' as AdminSection, label: 'Users' },
            ]).map((tab) => {
              const isActive = activeSection === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveSection(tab.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    isActive
                      ? 'text-primary dark:text-blue-400 border-primary'
                      : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-primary dark:hover:text-blue-400 hover:border-primary'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
      {/* Company Information Section */}
      {activeSection === 'company' && company && (
        <div className="bg-white dark:bg-dark-surface p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-secondary dark:text-dark-text">Company Information</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Company name, domain, licenses, and addresses</p>
            </div>
            <button
              onClick={() => setShowAddressForm(true)}
              className="px-3 py-1.5 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            >
              Add Address
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Company Name:</span>
              <p className="text-secondary dark:text-dark-text">{company.name}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Domain:</span>
              <p className="text-secondary dark:text-dark-text">{company.domain}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Licenses:</span>
              <p className="text-secondary dark:text-dark-text">{company.licenseCount}</p>
            </div>
          </div>

          {/* Company Addresses */}
          <div>
            <h3 className="text-lg font-medium text-secondary dark:text-dark-text mb-3">Company Addresses</h3>
            {company.addresses && company.addresses.length > 0 ? (
              <div className="space-y-3">
                {company.addresses.map((address) => (
                  <div key={address.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-secondary dark:text-dark-text">
                            {address.label || 'Address'}
                          </span>
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 capitalize">
                            {address.addressType}
                          </span>
                          {address.isDefault && (
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-secondary dark:text-dark-text">
                          {address.street1}
                          {address.street2 && `, ${address.street2}`}
                        </p>
                        <p className="text-sm text-secondary dark:text-dark-text">
                          {address.city}, {address.state} {address.postalCode}
                        </p>
                        <p className="text-sm text-secondary dark:text-dark-text">
                          {address.country}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleEditAddress(address)}
                          className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                        >
                          Edit
                        </button>
                        {!address.isDefault && (
                          <button
                            onClick={() => handleSetDefaultAddress(address.id!)}
                            className="px-3 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
                          >
                            Set Default
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteAddress(address.id!)}
                          className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 italic">No addresses added yet.</p>
            )}
          </div>

        </div>
      )}

      {/* Company Branding Section */}
      {activeSection === 'branding' && company && company.branding !== undefined && (
        <div className="bg-white dark:bg-dark-surface p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <CompanyBrandingSection
            initialBranding={company.branding}
            onBrandingUpdate={(branding) => {
              setCompany({ ...company, branding });
            }}
            token={token}
          />
        </div>
      )}

      {/* AMS360 Configuration Section */}
      {activeSection === 'ams360' && company && isAms360Enabled && (
        <div className="bg-white dark:bg-dark-surface p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <AMS360Configuration token={token} />
        </div>
      )}

      {/* Proposal Templates Section */}
      {activeSection === 'templates' && company && (
        <div className="bg-white dark:bg-dark-surface p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
          <TemplateLibraryView isAdmin={true} token={token} />
        </div>
      )}

      {/* Company Prompt Templates Section */}
      {activeSection === 'prompt-templates' && company && <CompanyPromptTemplates />}

      {/* Usage Reports Section (Phase 3) */}
      {activeSection === 'reports' && (
        <UsageReportsSection adminEmail={user?.email || ''} />
      )}

      {/* User Management Section */}
      {activeSection === 'users' && (
      <div className="bg-white dark:bg-dark-surface p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-semibold text-secondary dark:text-dark-text">Company Users</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Create users, manage licenses, and reset access</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-secondary dark:text-dark-text">
              Licenses: <strong>{used}</strong>/{licenseCount} used · Available: <strong>{available}</strong>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-blue-600 transition-colors"
            >
              Create User
            </button>
          </div>
        </div>

      {/* Create User Form */}
      {showCreateForm && (
        <div className="mb-6 bg-white dark:bg-dark-surface p-6 rounded-lg shadow-md border border-gray-200 dark:border-dark-border">
          <h2 className="text-lg font-semibold text-secondary dark:text-dark-text mb-4">Create New User</h2>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  placeholder="Doe"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                placeholder="john.doe@company.com"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Email must match your company domain
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) => {
                  const role = e.target.value as 'user' | 'admin';
                  setFormData(prev => ({
                    ...prev,
                    role,
                    // Reset no-license flag when role isn't admin
                    consumesLicense: role === 'admin' ? prev.consumesLicense : true,
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {formData.role === 'admin' && (
              <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!formData.consumesLicense}
                  onChange={(e) => setFormData(prev => ({ ...prev, consumesLicense: !e.target.checked }))}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">Admin-only access (no license seat)</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    This admin can manage billing and users but cannot use platform features. Does not consume a license.
                  </span>
                </span>
              </label>
            )}
            <div className="rounded-md bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 p-3 text-sm text-blue-900 dark:text-blue-200">
              A temporary password will be generated and emailed to the user. They'll be required to change it on first login.
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({ email: '', firstName: '', lastName: '', role: 'user', consumesLicense: true });
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createLoading}
                className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {createLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-dark-surface text-secondary dark:text-dark-text">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left">
                <button
                  type="button"
                  onClick={() => toggleSort('name')}
                  className="inline-flex items-center gap-1 font-semibold hover:text-primary transition-colors"
                  title="Sort by name"
                >
                  Name
                  {sortBy === 'name' && <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              </th>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Role</th>
              <th className="px-4 py-2 text-left">License</th>
              <th className="px-4 py-2 text-left">LOB</th>
              <th className="px-4 py-2 text-left">
                <button
                  type="button"
                  onClick={() => toggleSort('status')}
                  className="inline-flex items-center gap-1 font-semibold hover:text-primary transition-colors"
                  title="Sort by status"
                >
                  Status
                  {sortBy === 'status' && <span className="text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </button>
              </th>
              <th className="px-4 py-2 text-left">2FA</th>
              <th className="px-4 py-2 text-left">Joined</th>
              <th className="px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {[...users].sort((a, b) => {
              const av = sortBy === 'status'
                ? a.status
                : `${a.firstName} ${a.lastName}`.trim().toLowerCase();
              const bv = sortBy === 'status'
                ? b.status
                : `${b.firstName} ${b.lastName}`.trim().toLowerCase();
              const cmp = av.localeCompare(bv);
              return sortDir === 'asc' ? cmp : -cmp;
            }).map(u => (
              <tr key={u.id} className="border-t border-gray-200 dark:border-dark-border">
                <td className="px-4 py-2">{u.firstName} {u.lastName}</td>
                <td className="px-4 py-2">{u.email}</td>
                <td className="px-4 py-2 capitalize">
                  {u.role}
                  {u.role === 'admin' && u.consumesLicense === false && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                      admin-only
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {u.role === 'admin' ? (
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={u.consumesLicense !== false}
                        onChange={(e) => setUserConsumesLicense(u.id, e.target.checked)}
                      />
                      <span>{u.consumesLicense !== false ? 'Uses seat' : 'No seat'}</span>
                    </label>
                  ) : (
                    <span className="text-xs text-gray-500 dark:text-gray-400">Uses seat</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <select
                    value={u.lineOfBusiness || 'both'}
                    onChange={(e) => updateUserLineOfBusiness(u.id, e.target.value)}
                    className="block w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="both">Both</option>
                    <option value="personal">Personal</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </td>
                <td className="px-4 py-2 capitalize">{u.status}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    u.twoFactorEnabled
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                  }`}>
                    {u.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td className="px-4 py-2">{new Date(u.createdAt).toLocaleDateString()}</td>
                <td className="px-4 py-2">
                  <div className="flex flex-wrap gap-1">
                    <button
                      onClick={() => openEditUser(u)}
                      className="px-2 py-1 text-xs bg-primary text-white rounded hover:bg-blue-600 transition-colors"
                    >
                      Edit
                    </button>
                    {u.status === 'pending' ? (
                      available > 0 ? (
                        <button
                          onClick={() => activateUser(u.id)}
                          className="px-2 py-1 text-xs bg-primary text-white rounded hover:bg-blue-600 transition-colors"
                        >
                          Assign License
                        </button>
                      ) : (
                        <button
                          disabled
                          className="px-2 py-1 text-xs bg-gray-500 text-white rounded cursor-not-allowed"
                        >
                          No Licenses
                        </button>
                      )
                    ) : u.status === 'active' ? (
                      <button
                        onClick={() => deactivateUser(u.id)}
                        className="px-2 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                      >
                        Revoke License
                      </button>
                    ) : null}

                    {u.id !== user?.id && (
                      u.role === 'admin' ? (
                        <button
                          onClick={() => changeUserRole(u.id, 'user')}
                          className="px-2 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors"
                        >
                          Make User
                        </button>
                      ) : (
                        <button
                          onClick={() => changeUserRole(u.id, 'admin')}
                          className="px-2 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors"
                        >
                          Make Admin
                        </button>
                      )
                    )}

                    {u.role !== 'admin' && (
                      <>
                        <button
                          onClick={() => handlePasswordChange(u.id)}
                          className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
                        >
                          Change Password
                        </button>
                        {u.twoFactorEnabled && (
                          <button
                            onClick={() => resetUser2FA(u.id)}
                            className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                          >
                            Reset 2FA
                          </button>
                        )}
                        <button
                          onClick={() => deleteUser(u.id)}
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
      )}

      {/* Address Form Modal */}
      {showAddressForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl shadow-xl max-w-lg w-full mx-auto my-8 p-6 max-h-full overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-secondary dark:text-dark-text">
                {editingAddress ? 'Edit Address' : 'Add New Address'}
              </h3>
              <button
                onClick={resetAddressForm}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddressFormSubmit} className="space-y-4">
              {/* Address Label */}
              <div>
                <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                  Address Label
                </label>
                <input
                  type="text"
                  name="label"
                  value={addressFormData.label}
                  onChange={handleAddressInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                  placeholder="e.g., Headquarters, Main Office"
                />
              </div>

              {/* Address Type */}
              <div>
                <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                  Address Type
                </label>
                <select
                  name="addressType"
                  value={addressFormData.addressType}
                  onChange={handleAddressInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                >
                  <option value="office">Office</option>
                  <option value="primary">Primary</option>
                  <option value="billing">Billing</option>
                  <option value="shipping">Shipping</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Street Address 1 */}
              <div>
                <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                  Street Address *
                </label>
                <input
                  type="text"
                  name="street1"
                  value={addressFormData.street1}
                  onChange={handleAddressInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                  placeholder="Enter street address"
                  required
                />
              </div>

              {/* Street Address 2 */}
              <div>
                <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                  Street Address 2
                </label>
                <input
                  type="text"
                  name="street2"
                  value={addressFormData.street2}
                  onChange={handleAddressInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                  placeholder="Apt, suite, floor (optional)"
                />
              </div>

              {/* City, State, ZIP row */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={addressFormData.city}
                    onChange={handleAddressInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                    placeholder="City"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={addressFormData.state}
                    onChange={handleAddressInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                    placeholder="State"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={addressFormData.postalCode}
                    onChange={handleAddressInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                    placeholder="ZIP"
                    required
                  />
                </div>
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                  Country
                </label>
                <select
                  name="country"
                  value={addressFormData.country}
                  onChange={handleAddressInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="Mexico">Mexico</option>
                </select>
              </div>

              {/* Set as Default */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isDefault"
                  id="isDefault"
                  checked={addressFormData.isDefault}
                  onChange={handleAddressInputChange}
                  className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary dark:focus:ring-primary dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="isDefault" className="ml-2 text-sm font-medium text-secondary dark:text-dark-text">
                  Set as default address
                </label>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetAddressForm}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-secondary dark:text-dark-text rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  {editingAddress ? 'Update Address' : 'Add Address'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Profile Modal */}
      {editingUser && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={closeEditUser}
        >
          <div
            className="bg-white dark:bg-dark-surface rounded-lg shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="edit-user-title"
          >
            <div className="flex items-start justify-between mb-4">
              <h3 id="edit-user-title" className="text-lg font-semibold text-secondary dark:text-dark-text">
                Edit user profile
              </h3>
              <button
                onClick={closeEditUser}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {editError && (
              <div className="mb-3 p-2 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded">
                {editError}
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); saveEditUser(); }}
              className="space-y-3"
            >
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  First name
                </label>
                <input
                  type="text"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Last name
                </label>
                <input
                  type="text"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  required
                />
                {company?.domain && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Must end with @{company.domain}
                  </p>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeEditUser}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded hover:bg-blue-600 disabled:opacity-50 transition-colors"
                >
                  {editSaving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Admin; 